/**
 * Admin Dashboard — Live Operational Metrics
 *
 * Time-windowed metrics (24h/48h/72h) with urgent attention banner,
 * auto-refresh every 60s, and quick navigation links.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';

import {
  fetchOpsSummary,
  fetchDashboardActivity,
  type OpsSummary,
  type DashboardActivity,
  type TimeWindow,
} from '../../services/dashboardService';

// ============================================================================
// Helpers
// ============================================================================

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatSecondsAgo(seconds: number): string {
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ago`;
}

type ActivityType = 'customer' | 'fulfillment' | 'billing' | 'system' | 'ai';

function getActivityIcon(type: ActivityType): React.ReactNode {
  switch (type) {
    case 'customer':
      return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    case 'billing':
      return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'fulfillment':
      return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'ai':
      return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      );
    default:
      return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
  }
}

function getActivityColor(type: ActivityType): string {
  switch (type) {
    case 'customer': return 'hsl(var(--color-primary-400))';
    case 'billing': return 'hsl(var(--color-success-400))';
    case 'fulfillment': return 'hsl(210, 80%, 60%)';
    case 'ai': return 'hsl(280, 70%, 60%)';
    default: return 'hsl(var(--color-neutral-400))';
  }
}

// ============================================================================
// Urgent Banner
// ============================================================================

interface UrgentBannerProps {
  basePath: string;
  unreadTickets: number;
  sosOutstanding: number;
  pendingTasks: number;
}

function UrgentBanner({ basePath, unreadTickets, sosOutstanding, pendingTasks }: UrgentBannerProps) {
  const hasUrgent = unreadTickets > 0 || sosOutstanding > 0 || pendingTasks > 5;

  if (!hasUrgent) {
    return (
      <div
        className="flex items-center gap-2 rounded-lg border px-4 py-2.5"
        style={{
          backgroundColor: 'hsla(var(--color-success-400), 0.06)',
          borderColor: 'hsla(var(--color-success-400), 0.2)',
        }}
      >
        <svg className="h-4 w-4 shrink-0" style={{ color: 'hsl(var(--color-success-400))' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span className="text-sm" style={{ color: 'hsl(var(--color-success-400))' }}>
          All clear — no items need immediate attention
        </span>
      </div>
    );
  }

  const items: React.ReactNode[] = [];
  if (unreadTickets > 0) {
    items.push(
      <Link key="tickets" to={`${basePath}/support`} className="underline hover:opacity-80">
        {unreadTickets} unread ticket{unreadTickets !== 1 ? 's' : ''}
      </Link>
    );
  }
  if (sosOutstanding > 0) {
    items.push(
      <Link key="sos" to={`${basePath}/sos`} className="underline hover:opacity-80">
        {sosOutstanding} SOS request{sosOutstanding !== 1 ? 's' : ''}
      </Link>
    );
  }
  if (pendingTasks > 5) {
    items.push(
      <Link key="tasks" to={`${basePath}/fulfillment`} className="underline hover:opacity-80">
        {pendingTasks} pending tasks
      </Link>
    );
  }

  return (
    <div
      className="flex items-center gap-2 rounded-lg border px-4 py-2.5"
      style={{
        backgroundColor: 'hsla(var(--color-error-400), 0.08)',
        borderColor: 'hsla(var(--color-error-400), 0.3)',
      }}
    >
      <svg className="h-4 w-4 shrink-0" style={{ color: 'hsl(var(--color-error-400))' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
      <span className="text-sm" style={{ color: 'hsl(var(--color-error-400))' }}>
        {items.reduce<React.ReactNode[]>((acc, item, i) => {
          if (i > 0) acc.push(<span key={`sep-${i}`}> &middot; </span>);
          acc.push(item);
          return acc;
        }, [])}
      </span>
    </div>
  );
}

// ============================================================================
// Ops Metric Card (time-windowed)
// ============================================================================

interface OpsMetricCardProps {
  label: string;
  window: TimeWindow;
  href: string;
  color: string;
  prefix?: string;
  subtitle?: string;
  urgent?: boolean;
}

function OpsMetricCard({ label, window: tw, href, color, prefix = '', subtitle, urgent }: OpsMetricCardProps) {
  return (
    <Link to={href} className="block hover:opacity-90 transition-opacity">
      <div
        className="rounded-lg border p-4 h-full"
        style={{
          backgroundColor: 'hsl(var(--admin-surface))',
          borderColor: urgent ? color : 'hsl(var(--admin-border))',
          borderLeftWidth: urgent ? '3px' : undefined,
          borderLeftColor: urgent ? color : undefined,
        }}
      >
        <p className="text-sm" style={{ color: 'hsl(var(--color-neutral-400))' }}>{label}</p>
        <p className="mt-1.5 text-3xl font-bold" style={{ color }}>
          {prefix}{tw.last24h.toLocaleString()}
        </p>
        <p className="mt-1 text-xs" style={{ color: 'hsl(var(--color-neutral-500))' }}>
          48h: {prefix}{tw.last48h.toLocaleString()} &middot; 72h: {prefix}{tw.last72h.toLocaleString()}
        </p>
        {subtitle && (
          <p className="mt-1 text-xs" style={{ color: 'hsl(var(--color-neutral-400))' }}>
            {subtitle}
          </p>
        )}
      </div>
    </Link>
  );
}

// ============================================================================
// Total Card (simple metric)
// ============================================================================

interface TotalCardProps {
  label: string;
  value: string | number;
  sub?: string;
  color: string;
}

function TotalCard({ label, value, sub, color }: TotalCardProps) {
  return (
    <div
      className="rounded-lg border p-4"
      style={{
        backgroundColor: 'hsl(var(--admin-surface))',
        borderColor: 'hsl(var(--admin-border))',
      }}
    >
      <p className="text-sm" style={{ color: 'hsl(var(--color-neutral-400))' }}>{label}</p>
      <p className="mt-1 text-2xl font-bold" style={{ color }}>{value}</p>
      {sub && (
        <p className="mt-1 text-xs" style={{ color: 'hsl(var(--color-neutral-400))' }}>{sub}</p>
      )}
    </div>
  );
}

// ============================================================================
// Quick Actions
// ============================================================================

function QuickActions({ basePath }: { basePath: string }) {
  const links = [
    { label: 'Control Room', to: `${basePath}/control-room` },
    { label: 'Support', to: `${basePath}/support` },
    { label: 'Fulfillment', to: `${basePath}/fulfillment` },
    { label: 'SOS', to: `${basePath}/sos` },
    { label: 'Customers', to: `${basePath}/customers` },
  ];

  return (
    <div className="flex flex-wrap gap-2">
      {links.map((link) => (
        <Link
          key={link.label}
          to={link.to}
          className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors hover:opacity-80"
          style={{
            backgroundColor: 'hsl(var(--admin-hover))',
            color: 'hsl(var(--color-neutral-800))',
          }}
        >
          {link.label}
        </Link>
      ))}
    </div>
  );
}

// ============================================================================
// Activity Feed
// ============================================================================

interface ActivityFeedProps {
  activity: DashboardActivity[];
  isLoading: boolean;
}

function ActivityFeed({ activity, isLoading }: ActivityFeedProps) {
  if (isLoading) {
    return (
      <div className="flex h-24 items-center justify-center">
        <svg
          className="h-5 w-5 animate-spin"
          style={{ color: 'hsl(var(--color-neutral-400))' }}
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (activity.length === 0) {
    return (
      <p className="py-4 text-center text-sm" style={{ color: 'hsl(var(--color-neutral-500))' }}>
        No recent activity
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {activity.map((item) => (
        <div key={item.id} className="flex items-start gap-3">
          <div
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
            style={{
              backgroundColor: `${getActivityColor(item.type)}20`,
              color: getActivityColor(item.type),
            }}
          >
            {getActivityIcon(item.type)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm" style={{ color: 'hsl(var(--color-neutral-800))' }}>
              {item.description}
            </p>
            <p className="mt-0.5 text-xs" style={{ color: 'hsl(var(--color-neutral-500))' }}>
              {formatTimeAgo(item.timestamp)}
              {item.actor && ` \u00B7 ${item.actorType === 'ai' ? 'Shadow Operator' : item.actor}`}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Main Dashboard Component
// ============================================================================

export function AdminDashboard() {
  const basePath = window.location.pathname.startsWith('/admin') ? '/admin' : '';

  const [ops, setOps] = useState<OpsSummary | null>(null);
  const [activity, setActivity] = useState<DashboardActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<number>(Date.now());
  const [secondsAgo, setSecondsAgo] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [opsData, activityData] = await Promise.allSettled([
        fetchOpsSummary(),
        fetchDashboardActivity(15),
      ]);

      if (opsData.status === 'fulfilled') {
        setOps(opsData.value);
        setError(null);
      } else {
        setError('Failed to load operational metrics');
      }

      if (activityData.status === 'fulfilled') {
        setActivity(activityData.value);
      }
    } catch (err) {
      console.error('Dashboard load failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setIsLoading(false);
      setActivityLoading(false);
      setLastRefresh(Date.now());
      setSecondsAgo(0);
    }
  }, []);

  useEffect(() => {
    void loadData();

    // Auto-refresh every 60s
    intervalRef.current = setInterval(() => {
      void loadData();
    }, 60_000);

    // Update "seconds ago" ticker
    tickRef.current = setInterval(() => {
      setSecondsAgo((prev) => prev + 1);
    }, 1_000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [loadData]);

  // Suppress unused variable lint — lastRefresh drives setSecondsAgo(0) on refresh
  void lastRefresh;

  const handleRefresh = () => {
    setIsLoading(true);
    setActivityLoading(true);
    void loadData();
  };

  if (isLoading && !ops) {
    return (
      <div className="flex h-64 items-center justify-center">
        <svg
          className="h-8 w-8 animate-spin"
          style={{ color: 'hsl(var(--color-primary-400))' }}
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- boolean context, empty string should be falsy
  if (error || !ops) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <p style={{ color: 'hsl(var(--color-error-400))' }}>
            {error ?? 'Failed to load dashboard data'}
          </p>
          <button
            onClick={handleRefresh}
            className="mt-4 rounded-md px-4 py-2 text-sm"
            style={{
              backgroundColor: 'hsl(var(--admin-hover))',
              color: 'hsl(var(--color-neutral-800))',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold" style={{ color: 'hsl(var(--color-neutral-900))' }}>
            Operations
          </h1>
          <span
            className="rounded px-2 py-0.5 text-xs"
            style={{
              backgroundColor: 'hsl(var(--admin-hover))',
              color: 'hsl(var(--color-neutral-400))',
            }}
          >
            Updated {formatSecondsAgo(secondsAgo)}
          </span>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="rounded-md p-2 transition-colors hover:opacity-80 disabled:opacity-40"
          style={{
            backgroundColor: 'hsl(var(--admin-hover))',
            color: 'hsl(var(--color-neutral-300))',
          }}
          title="Refresh"
        >
          <svg
            className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* System Health Widget (Phase 7.2) */}
      <div
        className="flex items-center justify-between rounded-lg border px-4 py-3"
        style={{
          backgroundColor: 'hsl(var(--admin-surface))',
          borderColor: 'hsl(var(--admin-border))',
        }}
      >
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{
                backgroundColor:
                  ops.fulfillment.pendingTotal > 10
                    ? 'hsl(var(--color-warning-400))'
                    : 'hsl(var(--color-success-400))',
              }}
            />
            <span className="text-sm font-medium" style={{ color: 'hsl(var(--color-neutral-700))' }}>
              System Health
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs" style={{ color: 'hsl(var(--color-neutral-400))' }}>
            <span>{ops.customers.total} customers</span>
            <span className="opacity-30">|</span>
            <span>${ops.revenue.total.toLocaleString()} revenue</span>
            <span className="opacity-30">|</span>
            <span>{ops.fulfillment.pendingTotal} pending tasks</span>
            <span className="opacity-30">|</span>
            <span>{ops.supportTickets.unread} unread tickets</span>
          </div>
        </div>
        <span className="text-xs" style={{ color: 'hsl(var(--color-neutral-400))' }}>
          {formatSecondsAgo(secondsAgo)}
        </span>
      </div>

      {/* Urgent Banner */}
      <UrgentBanner
        basePath={basePath}
        unreadTickets={ops.supportTickets.unread}
        sosOutstanding={ops.sosRequests.outstanding}
        pendingTasks={ops.fulfillment.pendingTotal}
      />

      {/* Time-windowed metrics grid (2x3) */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <OpsMetricCard
          label="New Customers"
          window={ops.customers.new}
          href={`${basePath}/customers`}
          color="hsl(var(--color-primary-400))"
        />
        <OpsMetricCard
          label="Payments"
          window={ops.payments.recent}
          href={`${basePath}/payments`}
          color="hsl(var(--color-success-400))"
        />
        <OpsMetricCard
          label="Revenue (24h)"
          window={ops.revenue.recent}
          href={`${basePath}/payments`}
          color="hsl(var(--color-accent-400))"
          prefix="$"
        />
        <OpsMetricCard
          label="Unread Tickets"
          window={ops.supportTickets.newRecent}
          href={`${basePath}/support`}
          color="hsl(var(--color-warning-400))"
          subtitle={`${ops.supportTickets.open} open total`}
          urgent={ops.supportTickets.unread > 0}
        />
        <OpsMetricCard
          label="SOS Requests"
          window={ops.sosRequests.recent}
          href={`${basePath}/sos`}
          color="hsl(var(--color-error-400))"
          subtitle={ops.sosRequests.outstanding > 0 ? `${ops.sosRequests.outstanding} outstanding` : undefined}
          urgent={ops.sosRequests.outstanding > 0}
        />
        <OpsMetricCard
          label="New Tasks"
          window={ops.fulfillment.newRecent}
          href={`${basePath}/fulfillment`}
          color="hsl(210, 80%, 60%)"
          subtitle={`${ops.fulfillment.pendingTotal} pending total`}
          urgent={ops.fulfillment.pendingTotal > 5}
        />
      </div>

      {/* Totals row */}
      <div className="grid gap-4 sm:grid-cols-3">
        <TotalCard
          label="Total Customers"
          value={ops.customers.total}
          color="hsl(var(--color-primary-400))"
        />
        <TotalCard
          label="Total Revenue"
          value={`$${ops.revenue.total.toLocaleString()}`}
          sub={`YTD $${ops.revenue.ytd.toLocaleString()} \u00B7 MTD $${ops.revenue.mtd.toLocaleString()}`}
          color="hsl(var(--color-success-400))"
        />
        <TotalCard
          label="Pending Tasks"
          value={ops.fulfillment.pendingTotal}
          color="hsl(210, 80%, 60%)"
        />
      </div>

      {/* Quick Actions */}
      <QuickActions basePath={basePath} />

      {/* Recent Activity */}
      <div
        className="rounded-lg border p-4"
        style={{
          backgroundColor: 'hsl(var(--admin-surface))',
          borderColor: 'hsl(var(--admin-border))',
        }}
      >
        <h2 className="font-semibold" style={{ color: 'hsl(var(--color-neutral-900))' }}>
          Recent Activity
        </h2>
        <div className="mt-3">
          <ActivityFeed activity={activity} isLoading={activityLoading} />
        </div>
      </div>
    </div>
  );
}
